import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { API_BASE_URL } from '../services/api';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'thisWeek', label: 'This Week' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'custom', label: 'Custom Range' },
];

const formatHeader = (key) =>
  key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const DOC_BASE_URL = 'https://rcs-dms.onlinetn.com/public/uploads/';
const DOC_COLUMNS = new Set(['order_list', 'dispatch_list', 'pc', 'uploaded_pc']);

const isDateString = (v) => {
  if (typeof v !== 'string') return false;
  // Matches ISO date / datetime
  return /^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2})?(\.\d+)?Z?)?$/.test(v);
};

const formatDateOnly = (v) => {
  if (!v) return '';
  const datePart = String(v).split(/[ T]/)[0];
  const [y, m, d] = datePart.split('-');
  if (!y || !m || !d) return String(v);
  return `${d}-${m}-${y}`;
};

const formatCell = (val, key) => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'object') return JSON.stringify(val);
  if (isDateString(val)) return formatDateOnly(val);
  return String(val);
};

const AllOrders = () => {
  const [period, setPeriod] = useState('thisWeek');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const fetchOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams();
      if (period === 'custom') {
        if (!fromDate || !toDate) {
          setError('Please select both From and To dates.');
          setLoading(false);
          return;
        }
        params.set('period', `${fromDate}:${toDate}`);
      } else {
        params.set('period', period);
      }
      const res = await fetch(`${API_BASE_URL}/indent/detail-list?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const result = await res.json();
      const data = Array.isArray(result?.data) ? result.data : Array.isArray(result) ? result : [];
      setRows(data);
    } catch (e) {
      setError('Failed to fetch orders.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const columns = useMemo(() => {
    if (rows.length === 0) return [];
    const keys = new Set();
    rows.forEach((r) => Object.keys(r || {}).forEach((k) => keys.add(k)));
    return Array.from(keys).filter((k) => !DOC_COLUMNS.has(k));
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      columns.some((c) => formatCell(r[c]).toLowerCase().includes(q))
    );
  }, [rows, columns, search]);

  const handleExport = async () => {
    if (filteredRows.length === 0) return;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Orders');

    ws.columns = columns.map((c) => ({ header: formatHeader(c), key: c, width: 18 }));
    const headerRow = ws.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBEAFE' } };
    headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

    filteredRows.forEach((r) => {
      const row = {};
      columns.forEach((c) => {
        const v = r[c];
        if (v === null || v === undefined || v === '') {
          row[c] = '';
        } else if (DOC_COLUMNS.has(c)) {
          row[c] = `${DOC_BASE_URL}${v}`;
        } else if (isDateString(v)) {
          row[c] = formatDateOnly(v);
        } else if (typeof v === 'object') {
          row[c] = JSON.stringify(v);
        } else {
          row[c] = v;
        }
      });
      ws.addRow(row);
    });

    ws.eachRow((row) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFCBD5E1' } },
          left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
          bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } },
          right: { style: 'thin', color: { argb: 'FFCBD5E1' } },
        };
      });
    });

    const buffer = await wb.xlsx.writeBuffer();
    const stamp = new Date().toISOString().slice(0, 10);
    const periodLabel = period === 'custom' ? `${fromDate}_to_${toDate}` : period;
    saveAs(new Blob([buffer]), `Orders_${periodLabel}_${stamp}.xlsx`);
  };

  const role = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return (u?.role || '').toLowerCase().trim();
    } catch {
      return '';
    }
  })();
  if (role !== 'rcs-admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-slate-800">Orders</h1>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Period</label>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="border border-gray-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                {PERIOD_OPTIONS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>

            {period === 'custom' && (
              <>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">From</label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="border border-gray-200 rounded-md px-3 py-2 text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">To</label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="border border-gray-200 rounded-md px-3 py-2 text-sm bg-white"
                  />
                </div>
              </>
            )}

            <button
              onClick={fetchOrders}
              disabled={loading}
              className={`px-4 py-2 text-sm font-semibold rounded-lg shadow-sm ${
                loading
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {loading ? 'Loading...' : 'Apply'}
            </button>

            <div className="ml-auto flex items-end gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Search</label>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search in results..."
                  className="border border-gray-200 rounded-md px-3 py-2 text-sm bg-white w-64"
                />
              </div>
              <button
                onClick={handleExport}
                disabled={filteredRows.length === 0}
                className={`px-4 py-2 text-sm font-semibold rounded-lg shadow-sm ${
                  filteredRows.length === 0
                    ? 'bg-gray-300 text-white cursor-not-allowed'
                    : 'bg-emerald-600 text-white hover:bg-emerald-700'
                }`}
              >
                Export Excel
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">
              {loading ? 'Loading...' : `${filteredRows.length} record${filteredRows.length === 1 ? '' : 's'}`}
            </span>
          </div>
          <div className="overflow-auto max-h-[70vh]">
            {columns.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">
                {loading ? 'Loading orders...' : 'No data for the selected period.'}
              </div>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    {columns.map((c) => (
                      <th key={c} className="text-left px-3 py-2 font-semibold text-slate-700 whitespace-nowrap border-b border-slate-200">
                        {formatHeader(c)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      {columns.map((c) => (
                        <td key={c} className="px-3 py-2 text-slate-700 whitespace-nowrap border-b border-slate-100">
                          {formatCell(r[c], c)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllOrders;
